import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import axios from 'config/axios';
import { auth, createUserWithEmailAndPassword, signInWithEmailAndPassword, googleSignIn } from 'config/firebase';
import CountryCodes from 'assets/json/countries.json';
import Google from 'assets/images/Google.webp';
import TextInput from 'components/TextInput';
import Dropdown from 'components/Dropdown';
import Button from 'components/Button';
import Backdrop from 'components/Backdrop';
import { useAppDispatch } from 'hooks/useStore';
import { signIn } from 'store/user';
import { setAlert, setError } from 'store/error';
import './index.css';

interface TestFinderAuthProps {
    onClose: () => void;
    onSuccess?: (studentTestId: string) => void;
}

function TestFinderAuth({ onClose, onSuccess }: TestFinderAuthProps) {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    
    const [isSignUp, setIsSignUp] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Sign Up fields
    const defaultCountry = CountryCodes.find(country => country.isoCode === 'US');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [country, setCountry] = useState(
        <div className='testFinderAuth__country' id={defaultCountry.isoCode}>
            <img src={defaultCountry.flag} />
            &nbsp;&nbsp;
            <p>{defaultCountry.name}</p>
        </div>
    );
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');

    const handleTestFinderNavigation = (studentTestId: string) => {
        if (onSuccess) {
            onSuccess(studentTestId);
        } else {
            navigate('/test-finder/test');
        }
    };

    const handleSignUp = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        
        if (password !== confirm) {
            dispatch(setAlert({
                response: {
                    data: {
                        message: 'The passwords don\'t match!'
                    }
                }
            }));
            return;
        }
        
        if (!country) {
            dispatch(setAlert({
                response: {
                    data: {
                        message: 'Please fill in all the required fields.'
                    }
                }
            }));
            return;
        }

        setIsSubmitting(true);
        
        createUserWithEmailAndPassword(auth, email, password)
            .then(userCredential => {
                axios.post('students/sign-up/', {
                    firstName: firstName,
                    lastName: lastName,
                    countryCode: country?.props.id,
                    phoneNumber: null,
                    gender: null,
                    birthDate: null,
                    parentInfo: null,
                    highSchool: null,
                    classOf: null,
                    postalCode: null
                })
                .then(res => {
                    dispatch(signIn(res.data?.user));
                    ReactGA.event('sign_up');
                    // Start the test immediately after signup
                    axios.post('testfinder/')
                        .then(testRes => {
                            handleTestFinderNavigation(testRes.data.studentTestId);
                        })
                        .catch(error => {
                            dispatch(setError(error));
                            setIsSubmitting(false);
                        });
                })
                .catch(error => {
                    userCredential.user.delete();
                    dispatch(setError(error));
                    setIsSubmitting(false);
                });
            })
            .catch(error => {
                dispatch(setAlert({
                    response: {
                        data: {
                            message: error.message
                        }
                    }
                }));
                setIsSubmitting(false);
            });
    };

    const handleSignIn = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        
        signInWithEmailAndPassword(auth, email, password)
            .then(() => {
                // Check if user has existing attempt or completed test, then start/resume
                axios.get('testfinder/')
                    .then(res => {
                        if (res.data.hasCompletedTest) {
                            // Test already completed, go to results
                            navigate('/sat-or-act');
                        } else if (res.data.hasExistingAttempt && res.data.existingAttemptId) {
                            // Resume existing test
                            handleTestFinderNavigation(res.data.existingAttemptId);
                        } else {
                            // Start new test
                            axios.post('testfinder/')
                                .then(testRes => {
                                    handleTestFinderNavigation(testRes.data.studentTestId);
                                })
                                .catch(error => {
                                    dispatch(setError(error));
                                    setIsSubmitting(false);
                                });
                        }
                    })
                    .catch(error => {
                        dispatch(setError(error));
                        setIsSubmitting(false);
                    });
            })
            .catch(error => {
                dispatch(setAlert({
                    response: {
                        data: {
                            message: error.message
                        }
                    }
                }));
                setIsSubmitting(false);
            });
    };

    const handleGoogleSignIn = () => {
        googleSignIn()
            .then(() => {
                // Check if user has existing attempt or completed test, then start/resume
                axios.get('testfinder/')
                    .then(res => {
                        if (res.data.hasCompletedTest) {
                            // Test already completed, go to results
                            navigate('/sat-or-act');
                        } else if (res.data.hasExistingAttempt && res.data.existingAttemptId) {
                            // Resume existing test
                            handleTestFinderNavigation(res.data.existingAttemptId);
                        } else {
                            // Start new test
                            axios.post('testfinder/')
                                .then(testRes => {
                                    handleTestFinderNavigation(testRes.data.studentTestId);
                                })
                                .catch(error => {
                                    dispatch(setError(error));
                                });
                        }
                    })
                    .catch(error => {
                        dispatch(setError(error));
                    });
            })
            .catch(error => {
                dispatch(setAlert({
                    response: {
                        data: {
                            message: error.message
                        }
                    }
                }));
            });
    };

    const resetForm = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setPassword('');
        setConfirm('');
    };

    return (
        <>
            <Backdrop onClick={onClose} />
            <div className='testFinderAuth'>
                <button 
                    className='testFinderAuth__close'
                    onClick={onClose}
                >
                    ×
                </button>
                
                <h2 className='testFinderAuth__title'>
                    {isSignUp ? 'Create an Account' : 'Sign In'}
                </h2>
                <p className='testFinderAuth__subtitle'>
                    {isSignUp 
                        ? 'Sign up to save your results and get your personalized recommendation.'
                        : 'Welcome back! Sign in to continue.'
                    }
                </p>
                
                <form 
                    className='testFinderAuth__form'
                    onSubmit={isSignUp ? handleSignUp : handleSignIn}
                >
                    {isSignUp && (
                        <>
                            <div className='testFinderAuth__nameGrid'>
                                <TextInput
                                    className='testFinderAuth__input'
                                    name='firstName'
                                    label='First Name'
                                    onChange={(event) => setFirstName(event.target.value)}
                                    required
                                    type='text'
                                    value={firstName}
                                />
                                <TextInput
                                    className='testFinderAuth__input'
                                    name='lastName'
                                    label='Last Name'
                                    onChange={(event) => setLastName(event.target.value)}
                                    required
                                    type='text'
                                    value={lastName}
                                />
                            </div>
                            <Dropdown
                                data={CountryCodes.map(country => (
                                    <div className='testFinderAuth__country' id={country.isoCode}>
                                        <img src={country.flag} />
                                        &nbsp;&nbsp;
                                        <p>{country.name}</p>
                                    </div>
                                ))}
                                onSelect={(value) => setCountry(value)}
                                label='Country'
                                required
                                value={country}
                            />
                        </>
                    )}
                    
                    <TextInput
                        className='testFinderAuth__input'
                        name='email'
                        label='Email Address'
                        onChange={(event) => setEmail(event.target.value)}
                        required
                        type='email'
                        value={email}
                    />
                    <TextInput
                        className='testFinderAuth__input'
                        name='password'
                        label='Password'
                        onChange={(event) => setPassword(event.target.value)}
                        required
                        type='password'
                        value={password}
                    />
                    
                    {isSignUp && (
                        <TextInput
                            className='testFinderAuth__input'
                            name='confirm'
                            label='Confirm Password'
                            onChange={(event) => setConfirm(event.target.value)}
                            required
                            type='password'
                            value={confirm}
                        />
                    )}
                    
                    <Button
                        className='testFinderAuth__button'
                        fullWidth
                        type='submit'
                        disabled={isSubmitting}
                    >
                        {isSubmitting 
                            ? (isSignUp ? 'CREATING ACCOUNT...' : 'SIGNING IN...') 
                            : (isSignUp ? 'CREATE ACCOUNT' : 'SIGN IN')
                        }
                    </Button>
                </form>
                
                <div className='testFinderAuth__divider'>
                    <span>or</span>
                </div>
                
                <button 
                    className='testFinderAuth__google'
                    type='button'
                    onClick={handleGoogleSignIn}
                >
                    <img 
                        src={Google} 
                        alt='Google' 
                        className='testFinderAuth__googleIcon' 
                    />
                    Sign in with Google
                </button>
                
                <div className='testFinderAuth__toggle'>
                    <p>
                        {isSignUp ? 'Already have an account?' : "Don't have an account?"}
                    </p>
                    <button
                        type='button'
                        onClick={() => {
                            setIsSignUp(!isSignUp);
                            resetForm();
                        }}
                    >
                        {isSignUp ? 'Sign In' : 'Sign Up'}
                    </button>
                </div>
            </div>
        </>
    );
}

export default TestFinderAuth;
